/**
 * User-friendly error message mapping utilities
 */

export interface ErrorMapping {
  code?: string;
  status?: number;
  pattern?: RegExp;
  message: string;
  action?: string;
}

/**
 * Common error mappings for user-friendly messages
 */
export const ERROR_MAPPINGS: ErrorMapping[] = [
  // Authentication errors (401 is handled specifically in getUserFriendlyErrorMessage)
  {
    code: 'INVALID_CREDENTIALS',
    message: 'Invalid email or password. Please check your credentials and try again.',
  },
  {
    code: 'ACCOUNT_LOCKED',
    message: 'Your account has been temporarily locked. Please contact support.',
  },

  // Authorization errors
  {
    status: 403,
    message: 'You do not have permission to perform this action.',
  },
  {
    code: 'KYC_NOT_APPROVED',
    message: 'Please complete KYC verification to access this feature.',
    action: 'redirect_kyc',
  },

  // Validation errors
  {
    code: 'VALIDATION_ERROR',
    message: 'Please check the form for errors and try again.',
  },
  {
    code: 'INVALID_PHONE_NUMBER',
    message: 'Please enter a valid Uganda mobile money number.',
  },
  {
    code: 'INVALID_AMOUNT',
    message: 'Please enter a valid amount between 1,000 and 5,000,000 UGX.',
  },
  {
    code: 'DUPLICATE_TRANSACTION',
    message: 'A similar transaction was recently processed. Please wait before trying again.',
  },

  // Transaction errors
  {
    code: 'INSUFFICIENT_BALANCE',
    message: 'Insufficient balance to complete this transaction.',
  },
  {
    code: 'TRANSACTION_FAILED',
    message: 'Transaction failed. Please try again or contact support if the problem persists.',
  },
  {
    code: 'PROVIDER_UNAVAILABLE',
    message: 'The mobile money provider is currently unavailable. Please try again later.',
  },
  {
    code: 'TRANSACTION_TIMEOUT',
    message: 'Transaction timed out. Please check the transaction status or try again.',
  },

  // Network errors
  {
    status: 429,
    message: 'Too many requests. Please wait a moment before trying again.',
  },
  {
    status: 500,
    message: 'Server error. Our team has been notified. Please try again later.',
  },
  {
    status: 502,
    message: 'Service temporarily unavailable. Please try again in a few minutes.',
  },
  {
    status: 503,
    message: 'Service is under maintenance. Please try again later.',
  },
  {
    pattern: /network|connection|timeout/i,
    message: 'Connection problem. Please check your internet connection and try again.',
  },

  // File upload errors
  {
    code: 'FILE_TOO_LARGE',
    message: 'File is too large. Please select a file smaller than 1MB.',
  },
  {
    code: 'INVALID_FILE_TYPE',
    message: 'Invalid file type. Please upload a PDF, JPG, or PNG file.',
  },
  {
    code: 'UPLOAD_FAILED',
    message: 'File upload failed. Please try again.',
  },

  // KYC errors
  {
    code: 'KYC_ALREADY_SUBMITTED',
    message: 'KYC verification is already in progress. Please wait for review.',
  },
  {
    code: 'KYC_REJECTED',
    message: 'KYC verification was rejected. Please review the feedback and resubmit.',
  },

  // API key errors
  {
    code: 'INVALID_API_KEY',
    message: 'Invalid API key. Please check your credentials.',
  },
  {
    code: 'API_KEY_EXPIRED',
    message: 'API key has expired. Please generate a new one.',
  },
];

/**
 * Get user-friendly error message from error object
 */
export function getUserFriendlyErrorMessage(error: any): string {
  // Check if error has a direct user message
  if (error?.response?.data?.message) {
    const apiMessage = error.response.data.message;

    // Look for specific error code mapping first
    const codeMapping = ERROR_MAPPINGS.find(
      (mapping) =>
        mapping.code &&
        (typeof apiMessage === 'string'
          ? apiMessage.includes(mapping.code)
          : Array.isArray(apiMessage) && apiMessage.some((m: string) => m.includes(mapping.code!)))
    );

    if (codeMapping) {
      return codeMapping.message;
    }

    // Direct server-provided message for client errors (e.g. 400 Bad Request, reserve breach, etc.)
    if (
      typeof apiMessage === 'string' &&
      (!error?.response?.status || error.response.status < 500)
    ) {
      return apiMessage;
    }
    if (Array.isArray(apiMessage) && (!error?.response?.status || error.response.status < 500)) {
      return apiMessage.join('. ');
    }
  }

  // Check for specific error codes in the error object
  const errorCode = error?.response?.data?.code || error?.code;
  if (errorCode) {
    const codeMapping = ERROR_MAPPINGS.find((mapping) => mapping.code === errorCode);
    if (codeMapping) {
      return codeMapping.message;
    }
  }

  // Special handling for 401 errors - distinguish between login failures and session expiration
  if (error?.response?.status === 401) {
    const url = error?.config?.url || '';
    const errorMessage = error?.response?.data?.message || '';

    // If this is a login request, show invalid credentials message
    if (url.includes('/auth/login') || url.includes('/login')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }

    // If the error message suggests invalid credentials
    if (
      errorMessage.toLowerCase().includes('invalid') ||
      errorMessage.toLowerCase().includes('credential') ||
      errorMessage.toLowerCase().includes('password') ||
      errorMessage.toLowerCase().includes('email')
    ) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }

    // Otherwise, it's likely a session expiration
    return 'Your session has expired. Please log in again.';
  }

  // Check other status code mappings (excluding 401 which we handled above)
  if (error?.response?.status && error.response.status !== 401) {
    const statusMapping = ERROR_MAPPINGS.find(
      (mapping) => mapping.status === error.response.status
    );

    if (statusMapping) {
      return statusMapping.message;
    }
  }

  // Check pattern mappings
  const errorMessage = error?.message || error?.response?.data?.error || String(error);
  const patternMapping = ERROR_MAPPINGS.find(
    (mapping) => mapping.pattern && mapping.pattern.test(errorMessage)
  );

  if (patternMapping) {
    return patternMapping.message;
  }

  // Default fallback message
  return 'Something went wrong. Please try again or contact support if the problem persists.';
}

/**
 * Get suggested action for an error
 */
export function getErrorAction(error: any): string | null {
  // Check if error has a direct user message with action
  if (error?.response?.data?.message) {
    const apiMessage = error.response.data.message;
    const codeMapping = ERROR_MAPPINGS.find(
      (mapping) => mapping.code && apiMessage.includes(mapping.code) && mapping.action
    );

    if (codeMapping) {
      return codeMapping.action || null;
    }
  }

  // Check status code mappings with actions
  if (error?.response?.status) {
    const statusMapping = ERROR_MAPPINGS.find(
      (mapping) => mapping.status === error.response.status && mapping.action
    );

    if (statusMapping) {
      return statusMapping.action || null;
    }
  }

  return null;
}

/**
 * Check if an error should trigger a specific action
 */
export function shouldRedirectToLogin(error: any): boolean {
  // Don't redirect on login page errors (invalid credentials)
  const url = error?.config?.url || '';
  if (url.includes('/auth/login') || url.includes('/login')) {
    return false;
  }

  // Redirect for session expiration (401 on authenticated endpoints)
  return getErrorAction(error) === 'redirect_login' || error?.response?.status === 401;
}

export function shouldRedirectToKYC(error: any): boolean {
  return getErrorAction(error) === 'redirect_kyc';
}

/**
 * Format validation errors for display
 */
export function formatValidationErrors(errors: Record<string, string[]>): string[] {
  const messages: string[] = [];

  Object.entries(errors).forEach(([field, fieldErrors]) => {
    fieldErrors.forEach((error) => {
      // Try to get user-friendly message for validation error
      const friendlyMessage = getUserFriendlyErrorMessage({ code: error });
      messages.push(`${field}: ${friendlyMessage}`);
    });
  });

  return messages;
}

/**
 * Create error context for logging
 */
export function createErrorContext(error: any, component?: string, action?: string) {
  return {
    component,
    action,
    status: error?.response?.status,
    code: error?.response?.data?.code || error?.code,
    message: error?.message,
    url: error?.config?.url,
  };
}
